import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { ChevronDown, Download, PrinterIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { DocumentToolbarGroup } from "./DocumentToolbarGroup";
import { toolbarTier } from "./documentToolbarTiers";

export type DocumentExportGroupProps = {
  onPrint: () => void;
  /**
   * Leave unset for documents that have no original to download — the group
   * is then a single print button at every width, since a menu holding one
   * item is a worse button.
   */
  originalPdfUrl?: string | null;
  /** Names the toolbar this group belongs to, e.g. `legislation`. */
  idPrefix: string;
};

const DOWNLOAD_LABEL = "Download original PDF";
const NO_PDF_LABEL = "Der findes ikke nogen original PDF at downloade";

/**
 * `aria-disabled` rather than `disabled`: the shared button turns off pointer
 * events when it is disabled, which takes the tooltip with it — and the
 * tooltip is the only thing that says *why* the button is unavailable. The
 * click handlers below no-op on their own.
 */
const INERT = "aria-disabled:cursor-not-allowed aria-disabled:opacity-50";
const PRINT_LABEL = "Print / Gem som PDF";

/**
 * "Eksportér": taking the document out of the app. It folds first, at the
 * full tier, because it is the group readers reach for least often.
 */
export const DocumentExportGroup = ({
  onPrint,
  originalPdfUrl,
  idPrefix,
}: DocumentExportGroupProps) => {
  const printButtonId = `${idPrefix}-print-btn`;
  const downloadButtonId = `${idPrefix}-download-original-pdf-btn`;
  const hasDownload = originalPdfUrl !== undefined;
  const openOriginalPdf = () =>
    originalPdfUrl &&
    window.open(originalPdfUrl, "_blank", "noopener,noreferrer");

  const printButton = (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="size-7"
          onClick={onPrint}
          id={printButtonId}
          aria-label={PRINT_LABEL}
        >
          <PrinterIcon className="size-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <p>{PRINT_LABEL}</p>
      </TooltipContent>
    </Tooltip>
  );

  if (!hasDownload) {
    return (
      <DocumentToolbarGroup label="Eksportér" className="shrink-0">
        {printButton}
      </DocumentToolbarGroup>
    );
  }

  return (
    <>
      <DocumentToolbarGroup
        label="Eksportér"
        className={cn("shrink-0", toolbarTier.fromFullFlex)}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className={cn("size-7", INERT)}
              aria-disabled={!originalPdfUrl}
              onClick={openOriginalPdf}
              id={downloadButtonId}
              aria-label={originalPdfUrl ? DOWNLOAD_LABEL : NO_PDF_LABEL}
            >
              <Download className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{originalPdfUrl ? "Download original PDF" : NO_PDF_LABEL}</p>
          </TooltipContent>
        </Tooltip>
        {printButton}
      </DocumentToolbarGroup>

      <DocumentToolbarGroup
        label="Eksportér"
        className={cn("shrink-0", toolbarTier.belowFull)}
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 gap-1 px-2"
              id={`${idPrefix}-export-menu`}
              aria-label="Eksportér dokumentet"
            >
              <Download className="size-4" />
              <ChevronDown className="size-3.5 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem
              className="cursor-pointer gap-2"
              disabled={!originalPdfUrl}
              onClick={openOriginalPdf}
              id={`${downloadButtonId}-menu-item`}
            >
              <Download className="size-4" />
              {originalPdfUrl ? DOWNLOAD_LABEL : NO_PDF_LABEL}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer gap-2"
              onClick={onPrint}
              id={`${printButtonId}-menu-item`}
            >
              <PrinterIcon className="size-4" />
              {PRINT_LABEL}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </DocumentToolbarGroup>
    </>
  );
};

export default DocumentExportGroup;
